import Stars from "../../UI/Stars/Stars";
import "./Rating.css";
import { ReactComponent as CloseIcon } from "../../assets/icons/cleanBee_close.svg";
import { connect } from "react-redux";
import { stateType } from "../../store";
import { clearReview } from "../../state_management/actions";
import { reviewType } from "../../state_management/reducers/common_reload";
import { useAsync } from "../../utilities/asyncFunc";
import { getServiceReviews } from "../../state_management/epics";
import { useContext, useEffect, useState } from "react";
import { reviewObj } from "../../state_management/actionTypes";
import { dateFormat } from "../../utilities/misc";
import { LocaleContext } from "../../AppProvider";

const Rating = ({
  clearReview,
  review
}: {
  clearReview: () => void;
  review: reviewType | null;
}) => {
  const { t, locale } = useContext(LocaleContext);
  const [reviewList, setReviewList] = useState<reviewObj[]>([]);

  /**
   * The array has the 5 (highest rating) at index [0] and 1 (lowest rating) at index [4]
   * The array returned is of length 5
   * @returns An array of rating counts. That is rate count distribution
   */
  const rateDistArray = (rateNumArray: number[]) => {
    /**
     * Normalize the number
     * By normalize, values should be whole numbers and should span from 1-5
     */
    const normalize = (num: number) => {
      if (num > 5) {
        return 5;
      } else if (num < 1) {
        return 1;
      } else {
        return Math.floor(num);
      }
    };

    // Initialize an array with length of 5 and fill it with zeros
    // 0s here indicate the number of seen rating
    // The indexes are a reverse with rating of 5 being at index [0] and rating of 1 at index [4]
    // i.e, if a rating of 2 is seen, the value at index [3] is increased by 1
    let arrayDist: number[] = Array(5).fill(0);

    // 5 - normalize(num) was used to get the index of the rate
    // That is rate of 4 translates to {{5 - normalize(4)}} = [1]
    rateNumArray.forEach((num) => {
      const index = 5 - normalize(num);
      arrayDist[index] = arrayDist[index] + 1;
    });

    return arrayDist;
  };

  const getRateNumArray = () => {
    return reviewList.map((review) => review.rate);
  };

  /**
   *
   * @returns Average rating
   */
  const avgRating = (): number => {
    // Initial average at 0
    let rateTotal = 0;

    // Get all the ratings from the review list
    const rateNumArray = getRateNumArray();

    // Get rating distribution
    const ratingDistribution = rateDistArray(rateNumArray);

    // Get the total rating value (add all rating together)
    const rateCounts = ratingDistribution.reduce(
      (num1, num2) => num1 + num2,
      0
    );

    // Rating distribution. Recall that the count of the highest rating (5) is at the [0] index
    ratingDistribution.forEach((rateCount, n) => {
      // Get the rating. At index [0], rating is 5, at index [3], rating is 2
      const rate = 5 - n;

      rateTotal = rate * rateCount + rateTotal;
    });

    return Number((rateTotal / rateCounts).toFixed(1));
  };

  const reviewsRes = useAsync(
    {
      asyncFunc: getServiceReviews,
      funcParams: { serviceId: review?.serviceId },
      immediate: true
    },
    [review?.serviceId]
  );

  useEffect(() => {
    const comments = reviewsRes.data?.data;
    if (reviewsRes.data && comments) {
      setReviewList([...comments]);
    }
  }, [reviewsRes.data]);

  /**
   *
   * @returns The number of review that have comments attached
   */
  const commentNum = (() => {
    let count = 0;
    reviewList.forEach((review) => {
      if (review.comment) {
        count++;
      }
    });
    return count;
  })();

  useEffect(() => {
    if (!review) {
      setReviewList([]);
    }
  }, [review]);

  return true ? (
    <></>
  ) : (
    <div
      onClick={clearReview}
      className={`ratingPage_wrapper center ${review ? `` : `noShow`}`}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="ratingPage_container"
      >
        <div onClick={clearReview} className="float_close center">
          <CloseIcon className="img_div_contain close_icon" />
        </div>
        {reviewList.length ? (
          <>
            <div className="rating_dist">
              <div className="rate_count fdCol">
                <div className="center">
                  <span className="count_rate_avg">{avgRating()}/5</span>
                </div>
                <div className="center">
                  <Stars rating={avgRating()} />
                </div>
                <div className="smallText center">
                  ({reviewList.length} review
                  {reviewList.length === 1 ? `` : `s`})
                </div>
              </div>
              <div className="rate_count_dist_wrapper flex1">
                {rateDistArray(getRateNumArray()).map((num, n) => (
                  <div key={n} className="rate_count_dist">
                    <div className="center">{5 - n}</div>
                    <div className="rateProgress flex">
                      <div
                        style={{
                          width: `${
                            (num * 100) /
                            rateDistArray(getRateNumArray()).reduce(
                              (num1, num2) => num1 + num2,
                              0
                            )
                          }%`
                        }}
                        className="rateBar"
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rating_info_wrapper flex1">
              <div className="head">
                {t("REVIEWS FOR {{service_name}}", {
                  "{{service_name}}": review?.service || ""
                })}
                <span>
                  {commentNum === 1
                    ? t("1 comment")
                    : `${commentNum} ${t("comments")}`}
                </span>
              </div>
              <div className="body">
                {reviewList.map((review, n) =>
                  review.comment ? (
                    <div key={n} className="rating_comment">
                      <div className="top flex">
                        <div className="flex1">
                          <div className="title">
                            {review.customer.firstname &&
                            review.customer.lastname
                              ? `${review.customer.firstname} ${review.customer.lastname}`
                              : t("Anonymous")}
                          </div>
                          <div>
                            <Stars rating={review.rate} />
                          </div>
                        </div>
                        <div className="date">
                          {dateFormat(new Date(review.created_at), locale)}
                        </div>
                      </div>
                      <p>{review.comment}</p>
                    </div>
                  ) : (
                    ``
                  )
                )}
              </div>
            </div>
          </>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: stateType) => ({
  review: state.commonReloadReducer.review
});
const mapDispatchToProps = (dispatch: any) => ({
  clearReview: () => {
    dispatch(clearReview());
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(Rating);
